import React, { useState, useEffect } from 'react'
import styles from './styles.module.css'
import { useForm } from 'react-hook-form'
import { updateAccount, authorize, getAccount } from '../../../Api/index'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import CircularProgress from '@material-ui/core/CircularProgress'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import BaseAlert from '../../Snackbar/NewImplementations/BaseAlert/BaseAlert'
import useAlerts from '../../Snackbar/NewImplementations/HooksAlerts/useAlerts'
import ColorAlert from '../../Snackbar/NewImplementations/ColorAlert/ColorAlert'

function ApiKeyAlertBody ({ newToken }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 1800)
    return () => clearTimeout(t)
  }, [copied])

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        Your previous key is no longer valid. Be sure to update it in any apps
        or services that use the Datafiniti API.
      </div>
      <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.9 }}>
        New API key
      </div>
      <div
        style={{
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 4,
          padding: 8,
          fontFamily: 'monospace',
          fontSize: 12,
          wordBreak: 'break-all',
          marginBottom: 8
        }}
      >
        {newToken}
      </div>
      <CopyToClipboard text={newToken} onCopy={() => setCopied(true)}>
        <button
          type='button'
          style={{
            background: '#ffffff',
            color: '#2f7a45',
            border: 'none',
            borderRadius: 4,
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            minWidth: 110
          }}
        >
          {copied ? 'Copied!' : 'Copy key'}
        </button>
      </CopyToClipboard>
      <div style={{ marginTop: 14, fontSize: 12, opacity: 0.95 }}>
        When you press <strong>Ok</strong>, the page will refresh to apply your
        changes. Make sure you have copied your new key first.
      </div>
    </>
  )
}

function ProfileSettings () {
  const { data: dataAccount } = useQuery('firstFormAccount', getAccount)
  const queryCache = useQueryCache()
  const [loginLoading, setLoginLoading] = useState(false)

  const [alert, setAlert] = useState({
    open: false,
    title: '',
    body: '',
    color: '#48B966',
    actions: []
  })

  const { alerts, closeAlert, showValidationErrors, pushAlert } = useAlerts()

  const [queryData, setQueryData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    password: ''
  })

  const { register, handleSubmit, reset } = useForm({ mode: 'all' })

  const [login] = useMutation(userData => authorize(userData), {
    onSuccess: handleSuccessLogin,
    onError: handleError
  })

  async function handleSuccessLogin (data) {
    setLoginLoading(true)
    if (data.status >= 400) {
      setLoginLoading(false)
      pushAlert({
        type: 'error',
        message: data.data.errors?.[0] || 'Login failed.'
      })
    } else {
      const newToken = data.data.token
      window.localStorage.removeItem('dfp/session')
      localStorage.setItem('dfp/session', newToken)

      try {
        const fresh = await getAccount()
        if (fresh && fresh.status < 400) {
          queryCache.setQueryData('accountApp', fresh)
          queryCache.setQueryData('firstFormAccount', fresh)
        }
      } catch (e) {
        // non-fatal — modal still opens with the new token shown
      }

      setAlert({
        open: true,
        title: 'Your API key has been updated',
        body: <ApiKeyAlertBody newToken={newToken} />,
        color: '#48B966',
        actions: [
          {
            label: (
              <span
                style={{
                  display: 'inline-block',
                  background: '#ffffff',
                  color: '#2f7a45',
                  padding: '8px 22px',
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)'
                }}
              >
                Ok, refresh page
              </span>
            ),
            onClick: () => {
              setAlert(a => ({ ...a, open: false }))
              setTimeout(() => window.location.reload(), 300)
            }
          }
        ]
      })
    }
  }

  const [profileUpdate] = useMutation(
    ({ queryData }) => updateAccount(queryData),
    { onSuccess: handleSuccess, onError: handleError }
  )

  function handleSuccess (data) {
    if (data.status >= 400) {
      pushAlert({
        type: 'error',
        message: data.data.errors?.[0] || 'Update failed.'
      })
      setLoginLoading(false)
    } else {
      setLoginLoading(true)
      queryCache.cancelQueries('accountApp')
      queryCache.cancelQueries('firstFormAccount')
      login({ email: queryData.email, password: queryData.password })
    }
  }

  function handleError (error) {
    pushAlert({ type: 'error', message: error.message || 'An error occurred.' })
    setLoginLoading(false)
  }

  const onSubmit = data => {
    setLoginLoading(true)
    if (data.password !== data.password_repeat) {
      pushAlert({ type: 'error', message: 'Passwords do not match.' })
      setLoginLoading(false)
      return
    }
    const { password_repeat, ...cleanData } = data
    setQueryData(cleanData)
    profileUpdate({ queryData: cleanData })
  }

  useEffect(() => {
    if (dataAccount) {
      const newQueryData = {
        email: dataAccount.data.email,
        first_name: dataAccount.data.first_name,
        last_name: dataAccount.data.last_name,
        organization: dataAccount.data.organization,
        password: ''
      }
      setQueryData(newQueryData)
      reset({ ...newQueryData, password_repeat: '' })
    }
  }, [dataAccount, reset])

  return (
    <div style={{ marginTop: '18px' }}>
      <form
        autoComplete='off'
        onSubmit={handleSubmit(onSubmit, showValidationErrors)}
        className={styles.personalInfoContainer}
      >
        <div className={styles.spacingFormProfile}>
          <label className={styles.labelStyle}>First Name</label>
          <input
            name='first_name'
            autoComplete='nope'
            {...register('first_name', { required: 'First Name is required' })}
            className={styles.inputPersonalProfile}
          />
        </div>

        <div className={styles.spacingFormProfile}>
          <label className={styles.labelStyle}>Last Name</label>
          <input
            name='last_name'
            autoComplete='nope'
            defaultValue={queryData.last_name}
            {...register('last_name', { required: 'Last Name is required' })}
            className={styles.inputPersonalProfile}
          />
        </div>

        <div className={styles.spacingFormProfile}>
          <label className={styles.labelStyle}>Organization</label>
          <input
            name='organization'
            autoComplete='off'
            defaultValue={dataAccount?.data?.organization || ''}
            {...register('organization', {
              required: 'Organization is required'
            })}
            className={styles.inputPersonalProfile}
          />
        </div>

        <div className={styles.spacingFormProfile}>
          <label className={styles.labelStyle}>Email</label>
          <input
            name='email'
            autoComplete='off'
            defaultValue={dataAccount?.data?.email || ''}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,15}$/i,
                message: 'Invalid email format'
              }
            })}
            className={styles.inputPersonalProfile}
          />
        </div>

        <div className={styles.spacingFormProfile}>
          <label className={styles.labelStyle}>Password</label>
          <input
            name='password'
            type='password'
            autoComplete='new-password'
            {...register('password', { required: 'Password is required' })}
            className={styles.inputPersonalProfile}
          />
        </div>

        <div className={styles.spacingFormProfile}>
          <label className={styles.labelStyle}>Confirm Password</label>
          <input
            name='password_repeat'
            type='password'
            autoComplete='new-password'
            {...register('password_repeat', {
              required: 'Passwords do not match'
            })}
            className={styles.inputPersonalProfile}
          />
        </div>

        <div className={styles.containerUpdate}>
          {loginLoading ? (
            <button className={styles.buttonUpdateDisabled}>
              <CircularProgress
                style={{ width: '25px', height: '30px', color: 'white' }}
              />
            </button>
          ) : (
            <button type='submit' className={styles.buttonUpdate}>
              Update
            </button>
          )}
        </div>
      </form>

      <BaseAlert
        open={alert.open}
        title={alert.title}
        body={alert.body}
        color={alert.color}
        style={{
          width: '420px',
          maxWidth: '90vw',
          top: '50%',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          animation: 'none'
        }}
        actions={alert.actions}
        onClose={() => {}}
      />

      {alerts.map((a, i) => (
        <ColorAlert
          key={a.id}
          open={a.open}
          type={a.type}
          label={a.message}
          onClose={() => closeAlert(a.id)}
          style={{ bottom: 20 + i * 80, width: '320px' }}
        />
      ))}
    </div>
  )
}

export default ProfileSettings
